import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'

import styles from './ContentSettings.module.css'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Button from 'Design/components/Button'
import Select from 'Design/components/Select'
import FormErrorText from 'Design/components/FormErrorText'
import PageLoader from 'Layout/PageLoader'
import SwitchInput from 'Design/components/SwitchInput'
import HelpText from 'Design/components/HelpText'

import { AuthorizedPage } from '../../../../components/Authorized'

import { useGetAccountQuery, useUpdateAccountMutation } from 'Services/account'
import contentSettingsFormValidationSchema from './content-settings-form-validation-schema'

const ContentSettings = (props) => {
  const { account } = props

  const {
    register,
    formState: { errors, isDirty, isSubmitSuccessful },
    handleSubmit,
    reset,
    getValues,
    control
  } = useForm({
    defaultValues: {
      use_case: account.settings?.use_case || 'customer_service',
      max_ml_suggestions: account.settings?.max_ml_suggestions || 3,
      auto_sync_kbs: !!account.settings?.auto_sync_kbs
    },
    resolver: yupResolver(contentSettingsFormValidationSchema)
  })

  const [updateAccount, updateAccountResult] = useUpdateAccountMutation()

  const handleFormSubmit = (data) => {
    const payload = {
      settings: {
        ...(account.settings || {}),
        ...data
      }
    }

    const promise = updateAccount(payload).unwrap()
    toast.promise(promise, {
      loading: 'Updating content settings',
      success: 'Content settings updated successfully',
      error: 'Error while updating content settings. Try again.'
    })

    return promise
  }

  useEffect(() => {
    if (!isSubmitSuccessful) {
      return
    }

    reset({ ...getValues() })
  }, [isSubmitSuccessful, reset, getValues])

  const disableSaveButton = updateAccountResult.isLoading || !isDirty

  return (
    <div className={styles.Container}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.Form}>
        <div className={styles.HeadingRow}>
          <span className={styles.Heading}>{account.settings.ai_predictions && 'AI '}Bot Settings</span>
          <hr className={styles.HorizontalLine} />
        </div>
        {account.settings.ai_predictions ? (
          <div className={styles.FormSection}>
            <FormControl isInvalid={errors.use_case}>
              <Label>Use-case</Label>
              <Select {...register('use_case', { shouldUnregister: true })}>
                <option value='customer_service'>Customer Service</option>
                <option value='hardware_product_support'>Hardware Product Support</option>
              </Select>
              <HelpText>Setting the exact use-case will help the bot understand the user's questions better.</HelpText>
              {errors.use_case && <FormErrorText>{errors.use_case.message}</FormErrorText>}
            </FormControl>
          </div>
        ) : (
          <div className={styles.FormSection}>
            <FormControl isInvalid={errors.max_ml_suggestions}>
              <Label>Maximum number of answer suggestions</Label>
              <Input {...register('max_ml_suggestions')} />
              {errors.max_ml_suggestions && <FormErrorText>{errors.max_ml_suggestions.message}</FormErrorText>}
            </FormControl>
          </div>
        )}
        <br />
        <div className={styles.HeadingRow}>
          <span className={styles.Heading}>Knowledge Base Settings</span>
          <hr className={styles.HorizontalLine} />
        </div>
        <div className={styles.FormSection}>
          <FormControl isInvalid={errors.auto_sync_kbs}>
            <Controller
              control={control}
              name='auto_sync_kbs'
              render={({ field: { value, onChange } }) => {
                return (
                  <SwitchInput checked={value} onChange={onChange}>
                    Auto Sync Knowledge Base
                  </SwitchInput>
                )
              }}
            />
            <HelpText>Automatically syncs the knowledge base articles twice every day</HelpText>
            {errors.auto_sync_kbs && <FormErrorText>{errors.auto_sync_kbs.message}</FormErrorText>}
          </FormControl>
        </div>
        <Button variant='primary' type='submit' disabled={disableSaveButton}>
          Save
        </Button>
      </form>
    </div>
  )
}

const ContentSettingsContainer = () => {
  const { data: account = {}, isLoading } = useGetAccountQuery(null)

  if (isLoading) {
    return <PageLoader />
  }

  return <ContentSettings account={account} />
}

export default AuthorizedPage(ContentSettingsContainer, ['account_settings_modify'])
